import React from 'react';

const ProductCategories: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Product Categories</h1>
          <p className="text-gray-600 mt-2">
            The Product Categories page in DAS allows you to organize and manage your product catalog efficiently. Create hierarchical category structures with parent-child relationships to better organize your inventory and improve product searchability.
          </p>
        </div>

        {/* Main Product Categories List Image */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-6">
            <img
              src="/images/product-category/list.png"
              alt="Product Categories List Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
          </div>
          <p className="text-gray-600 text-sm">
            <strong>Product Categories Overview:</strong> The main interface showing all product categories with hierarchical structure. Parent categories are displayed with their subcategories indented below, making it easy to understand the organizational structure.
          </p>
        </div>

        {/* Product Categories Overview Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Product Categories Overview</h2>
          <p className="text-gray-600 mb-4">
            The Product Categories page provides a hierarchical view of your product organization system. Categories can have parent-child relationships, allowing you to create detailed classification structures that match your business needs.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Category Table Structure</h3>
          <p className="text-gray-600 mb-4">The categories table displays the following information:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Name:</strong> The category name with visual hierarchy indicators</li>
            <li><strong>Parent Category:</strong> Shows the parent category for subcategories (empty for top-level categories)</li>
            <li><strong>Actions:</strong> Quick access menu for editing, marking inactive, or deleting categories</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Hierarchical Display</h3>
          <p className="text-gray-600 mb-4">Categories are displayed with visual indicators showing their hierarchy:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Parent Categories:</strong> Displayed at the top level (e.g., "Beverage", "Food")</li>
            <li><strong>Subcategories:</strong> Indented with connecting lines showing parent relationship</li>
            <li><strong>Multiple Levels:</strong> Support for deep category structures as needed</li>
          </ul>
        </div>

        {/* Category Management Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Category Management Actions</h2>
          <p className="text-gray-600 mb-4">
            Each category row includes a dropdown menu with management options:
          </p>

          <div className="mb-6">
            <img
              src="/images/product-category/list-buttons.png"
              alt="Category Actions Menu"
              className="w-full max-w-2xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Category Actions:</strong> The dropdown menu appears when clicking the three dots icon, providing quick access to Edit, Mark As Inactive, and Delete options.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Available Actions</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Edit:</strong> Modify category name and parent category assignment</li>
            <li><strong>Mark As Inactive:</strong> Deactivate categories without deleting them</li>
            <li><strong>Delete:</strong> Permanently remove categories (with confirmation)</li>
          </ul>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Deleting a parent category will affect all its subcategories. Consider marking categories as inactive instead of deleting them to preserve historical data.
            </p>
          </div>
        </div>

        {/* Creating New Categories Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Product Categories</h2>
          <p className="text-gray-600 mb-4">
            Adding new categories is straightforward using the <strong>+ New</strong> button in the Product Categories page.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Create Category Process</h3>
          <p className="text-gray-600 mb-4">To create a new product category:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the <strong>+ New</strong> button in the top-right corner</li>
            <li>The "Create Category" dialog will open with the following options:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/product-category/form.png"
              alt="Create Category Dialog"
              className="w-full max-w-3xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Create Category Form:</strong> Modal dialog with category name field, "Make this a sub-category" checkbox, and parent category dropdown showing existing categories like "Beverage", "Soft Drink", "Beer", and "Food".
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Form Fields</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Name:</strong> Enter the category name (required field)</li>
            <li><strong>Make this a sub-category:</strong> Checkbox to create a subcategory</li>
            <li><strong>Parent Category:</strong> Select the parent category from dropdown (appears when sub-category is checked)</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Category Types</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Top-Level Categories:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Independent categories without parents</li>
                <li>Serve as main classification groups</li>
                <li>Examples: "Food", "Beverage", "Electronics"</li>
                <li>Leave sub-category checkbox unchecked</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Subcategories:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Categories that belong to a parent category</li>
                <li>Help create detailed classification</li>
                <li>Examples: "Soft Drink", "Beer" under "Beverage"</li>
                <li>Check sub-category box and select parent</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> The parent category dropdown shows existing categories with visual hierarchy indicators, making it easy to select the correct parent.
            </p>
          </div>

          <p className="text-gray-600 mb-4">After filling the form:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Review the category name and parent selection</li>
            <li>Click <strong>Save</strong> to create the category</li>
            <li>Click <strong>Cancel</strong> to discard and close the dialog</li>
            <li>The new category will appear in the categories list with proper hierarchy</li>
          </ol>
        </div>

        {/* Editing Categories Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Editing Product Categories</h2>
          <p className="text-gray-600 mb-4">
            Modify existing categories to reorganize your product structure or update category names.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Edit Category Process</h3>
          <p className="text-gray-600 mb-4">To edit an existing category:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the actions menu (three dots) for the category you want to edit</li>
            <li>Select <strong>Edit</strong> from the dropdown menu</li>
            <li>The "Edit Category" dialog will open with current information pre-filled:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/product-category/edit.png"
              alt="Edit Category Dialog"
              className="w-full max-w-3xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Edit Category Form:</strong> Modal dialog showing "Soft Drink" being edited with sub-category checkbox checked and "Beverage" selected as parent category.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Editable Properties</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Category Name:</strong> Update the display name</li>
            <li><strong>Sub-category Status:</strong> Convert between top-level and sub-category</li>
            <li><strong>Parent Assignment:</strong> Change the parent category or remove parent relationship</li>
          </ul>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Changes to category structure will not affect existing products assigned to these categories, but may impact how they appear in filtered views and reports.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCategories;
